import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'

import { useGetUserCountryAndStripeKeyQuery } from 'hooks/useQueries'

import { fetchVoucher } from '../../../actions/voucherActions'
import { media } from '../../../styles/media'

import ButtonLink from '../../../components/display/ButtonLink'
import InlineSpinner from '../../../components/display/InlineSpinner'
import Separator from '../../../components/display/Separator'
import BaseContainer from '../../../components/layout/Container'

import Voucher from '../Voucher'

const Container = styled(BaseContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PrimaryCallsToAction = styled.div`
  margin: 16px 0;
  display: flex;
  flex-direction: column;

  ${media.md`
    margin-bottom: 0;
    flex-direction: row;
  `}

  ${ButtonLink} {
    margin-top: 16px;
    min-width: 230px;

    &:not(:last-child) {
      ${media.md`
        margin-right: 32px;
      `}
    }
  }
`

const PrimarySection = styled.div`
  width: 100%;
  max-width: 618px;
  border: 1px solid ${({ theme }) => theme['-ds-neutral-500'].value};
  border-radius: 6px;
  padding: 16px;

  ${media.md`
    padding: 36px;
  `}

  display: flex;
  flex-direction: column;
  align-items: center;

  .voucher {
    width: 100%;
    height: 300px;

    ${media.md`
      height: 400px;
    `}
  }
`

const Info = styled.div`
  width: 100%;
  max-width: 800px;
  text-align: center;

  padding-top: 36px;
  padding-bottom: 64px;
  ${media.md`
    padding-top: 56px;
    padding-bottom: 80px;
  `}

  & > * {
    margin: 16px 0;
  }

  h3 {
    color: ${({ theme }) => theme.brand.forest};
    font-size: 20px;
    ${media.md`
      font-size: 30px;
    `}
  }
  p {
    line-height: 1.75;
    text-align: center;
    font-size: 16px;
    margin-bottom: 0;
    ${media.md`
      font-size: 20px;
    `}
  }
`

const Heading = styled.h1`
  color: ${({ theme }) => theme.brand.forest};
  text-align: center;
  font-size: 32px;
  font-weight: 500;
  line-height: 1.2;

  ${media.md`
    font-size: 40px;
  `}
`

const SecondaryHeading = styled(Heading)`
  font-size: 20px;
  ${media.md`
    font-size: 30px;
  `}
`

const HeadingWrapper = styled.div`
  padding: 36px 0;
  max-width: 600px;
`

const SecondaryCallsToAction = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${media.md`
    flex-direction: row;
    justify-content: center;
  `}

  ${ButtonLink} {
    width: 100%;

    ${media.md`
      width: 240px;

      &:not(:last-child) {
        margin-right: 24px;
      }
    `}
  }
`

const SecondarySection = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 64px 0;
  ${media.md`
    padding: 80px 0;
  `}

  ${SecondaryHeading} {
    margin-bottom: 44px;
  }

  ${SecondaryCallsToAction} {
  }
`

const DownloadButtonLink = styled(ButtonLink)`
  background-color: ${({ theme }) => theme.brand.jungle};
`

const VoucherCreateSuccess = ({ dispatch, match, vouchers, userSignedIn }) => {
  useEffect(() => {
    dispatch(fetchVoucher(match.params.voucherId))
    window.scrollTo(0, 0)
  }, [])

  const suggestable = vouchers.data && vouchers.data[vouchers.data.suggestable]
  const downloadUrl = vouchers?.data?.pdf_url

  const pollVoucherEndpoint = () =>
    dispatch(fetchVoucher(match.params.voucherId))

  const intervalId = useRef()

  // poll the endpoint while server is working on generating the voucher pdf and uploading it to storage
  useEffect(() => {
    if (!intervalId.current && userSignedIn) {
      intervalId.current = setInterval(pollVoucherEndpoint, 3000)
    }
  }, [intervalId, userSignedIn])

  // stop polling when server returns pdf url
  useEffect(() => {
    if (downloadUrl) {
      clearInterval(intervalId.current)
    }
  }, [downloadUrl, intervalId])

  // stop polling after 1 minute regardless
  useEffect(() => {
    setTimeout(() => {
      clearInterval(intervalId.current)
    }, 60000)
  }, [intervalId])

  const { data: countryData } = useGetUserCountryAndStripeKeyQuery()

  const countryCode = countryData?.currentUser?.defaultCountry?.code

  return (
    <Container>
      <HeadingWrapper>
        <Heading>Your gift voucher purchase was successful</Heading>
      </HeadingWrapper>

      <PrimarySection>
        <Voucher
          title={suggestable && suggestable.title}
          backgroundImage={suggestable && suggestable.promo_image.large}
        />
        <PrimaryCallsToAction>
          <DownloadButtonLink
            disabled={!downloadUrl}
            href={downloadUrl}
            target="_blank"
            download
            rounded
          >
            {downloadUrl ? 'Download Voucher' : <InlineSpinner color="white" />}
          </DownloadButtonLink>
          <ButtonLink href="/vouchers/create" ghost rounded>
            Buy Another Voucher
          </ButtonLink>
        </PrimaryCallsToAction>
      </PrimarySection>

      <Info>
        <h3>What&apos;s next?</h3>
        <p>
          Your gift voucher has also been emailed to{' '}
          <b>{vouchers?.data?.user?.email}</b> (check your junk folder if you
          can&apos;t find it). Print it out or send it to the lucky recipient,
          and then pat yourself on the back for giving such a great gift.
        </p>
      </Info>

      <Separator style={{ display: 'block', width: '100%' }} />

      <SecondarySection>
        <SecondaryHeading>
          While you&apos;re here, why not check out some of our great stories
          and listings and book your own soul-fulfilling escape&hellip;
        </SecondaryHeading>
        <SecondaryCallsToAction>
          <ButtonLink
            href={`/listings?type=accommodation&country=${countryCode}&state=all`}
            disabled={countryCode == null}
            rounded
          >
            {countryCode ? 'Book an Escape' : <InlineSpinner color="white" />}
          </ButtonLink>
        </SecondaryCallsToAction>
      </SecondarySection>
    </Container>
  )
}

export default connect(({ vouchers, auth }) => ({
  vouchers,
  userSignedIn: auth?.isSignedIn,
}))(VoucherCreateSuccess)
